import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { ApiResponse, PublicBoardResponse } from '@/api/types';
import { ArrowLeft, Loader2, Send, MapPin, Check } from 'lucide-react';
import WaveModal from '@/components/WaveModal';

function titleCase(s?: string | null): string {
  if (!s) return '';
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function Chips({ label, values }: { label: string; values?: (string | null)[] | null }) {
  const items = (values ?? []).filter(Boolean) as string[];
  if (items.length === 0) return null;
  return (
    <div>
      <div className="text-sm font-medium text-gray-700 mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {items.map((v, i) => (
          <span
            key={i}
            className="px-3 py-1.5 rounded-full text-sm bg-primary-50 text-primary-700"
          >
            {titleCase(v)}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function MatchDetail() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [showWaveModal, setShowWaveModal] = useState(false);

  // GET /identity-board/discovery/users/:userId → { user, board, waveStatus }.
  // Score/tier/shared-traits live only on the matches LIST, not here.
  const { data, isLoading, error } = useQuery({
    queryKey: ['match-detail', userId],
    queryFn: () =>
      api.get<ApiResponse<PublicBoardResponse>>(
        `/identity-board/discovery/users/${userId}`,
      ),
    enabled: !!userId,
  });

  const result = data?.data;
  const user = result?.user;
  const board = result?.board;
  const waveStatus = result?.waveStatus;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="text-center py-20">
        <h2 className="text-xl font-semibold mb-2">User not found</h2>
        <p className="text-gray-600 mb-4">This user may no longer be available.</p>
        <Link to="/lab/identity-board/discovery" className="btn-primary">
          Back to Discovery
        </Link>
      </div>
    );
  }

  const locationBits = [user.city, user.state].filter(Boolean).join(', ');
  const originBits = [user.originCity, user.originState].filter(Boolean).join(', ');

  return (
    <div className="max-w-2xl mx-auto">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Matches
      </button>

      {/* Profile Header */}
      <div className="card p-6 mb-6">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-400 to-accent-400 flex items-center justify-center text-white font-bold text-2xl flex-shrink-0">
            {user.name?.charAt(0) || '?'}
          </div>

          <div className="flex-1">
            <h1 className="text-2xl font-display font-bold mb-1">{user.name}</h1>
            {locationBits && (
              <p className="text-sm text-gray-600 flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {locationBits}
              </p>
            )}
            {originBits && (
              <p className="text-sm text-gray-500 mt-0.5">From {originBits}</p>
            )}
            {user.nativeLanguage && (
              <p className="text-sm text-gray-500 mt-0.5">
                Speaks {titleCase(user.nativeLanguage)}
              </p>
            )}
          </div>
        </div>

        {/* Wave CTA — driven by waveStatus */}
        <div className="mt-6 pt-6 border-t">
          {waveStatus?.canWave ? (
            <button
              onClick={() => setShowWaveModal(true)}
              className="btn-primary w-full py-3 flex items-center justify-center gap-2"
            >
              <Send className="w-5 h-5" />
              Send a Wave
            </button>
          ) : waveStatus?.status === 'mutual' ? (
            <Link
              to="/lab/identity-board/waves"
              className="w-full py-3 flex items-center justify-center gap-2 text-twins-dark bg-twins-light rounded-lg font-medium hover:bg-twins/20 transition-colors"
            >
              <Check className="w-5 h-5" />
              Connected — see contact info
            </Link>
          ) : waveStatus?.status === 'pending' ? (
            <div className="w-full py-3 text-center text-gray-600 bg-gray-100 rounded-lg text-sm font-medium">
              {waveStatus.isSender ? 'Wave sent — pending' : 'They waved at you'}
            </div>
          ) : waveStatus?.status === 'expired' ? (
            <div className="w-full py-3 text-center text-gray-600 bg-gray-100 rounded-lg text-sm font-medium">
              {waveStatus.retryAfter
                ? `The last wave expired. You can wave again from ${new Date(waveStatus.retryAfter).toLocaleDateString()}.`
                : 'The last wave expired.'}
            </div>
          ) : waveStatus?.status === 'declined' ? (
            // Only the member who declined sees this. The sender sees no status.
            <div className="w-full py-3 text-center text-gray-600 bg-gray-100 rounded-lg text-sm font-medium">
              You declined their wave
            </div>
          ) : null}
        </div>
      </div>

      {/* Public Board */}
      {board && (
        <div className="card p-6 space-y-5">
          <h2 className="text-lg font-semibold">Identity Board</h2>

          {/* Core */}
          {board.core && (board.core.eraLabel || board.core.schoolType || board.core.collegeType) && (
            <div className="space-y-1">
              <div className="text-sm font-medium text-gray-700">Roots</div>
              <p className="text-sm text-gray-600">
                {[
                  board.core.eraLabel,
                  board.core.eraVibe,
                  board.core.schoolType && `${titleCase(board.core.schoolType)} school`,
                  board.core.collegeType && `${titleCase(board.core.collegeType)} college`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
          )}

          {/* Journey */}
          {board.journey?.migrationNarrative && (
            <div className="space-y-1">
              <div className="text-sm font-medium text-gray-700">Journey</div>
              <p className="text-sm text-gray-600">
                {titleCase(board.journey.migrationNarrative)}
              </p>
            </div>
          )}

          {/* Interests */}
          {board.interests && (
            <div className="space-y-3">
              <Chips label="Sports" values={board.interests.sports} />
              <Chips label="Music" values={board.interests.musicGenres} />
              <Chips label="Entertainment" values={board.interests.entertainment} />
              <Chips label="Hobbies" values={board.interests.hobbies} />
            </div>
          )}

          {/* Lifestyle */}
          {board.lifestyle && (
            <div className="space-y-3">
              <Chips label="Touchstones" values={board.lifestyle.touchstones} />
              <Chips
                label="Looking to connect for"
                values={board.lifestyle.connectionIntents}
              />
              {board.lifestyle.socialStyle && (
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">
                    Social style
                  </div>
                  <p className="text-sm text-gray-600">
                    {titleCase(board.lifestyle.socialStyle)}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <WaveModal
        toUserId={userId ?? ''}
        toUserName={user.name}
        open={showWaveModal}
        onClose={() => setShowWaveModal(false)}
      />
    </div>
  );
}
